// Thin wrapper over the orval-generated client.
// Auth header injection and token refresh are handled by the axios
// interceptor in api/axiosInstance.ts.

import { getAdminAuth } from './api/generated/admin-auth/admin-auth';
import { getAdmin } from './api/generated/admin/admin';
import { getCredentials } from './api/generated/credentials/credentials';
import { getAdminReports } from './api/generated/admin-reports/admin-reports';

export type {
  CredentialReviewerDto,
  AdminLoginResponse,
  AdminTokenLoginResponse,
  AdminMfaRequiredResponse,
  AdminMfaEnrollmentRequiredResponse,
  AdminCredentialDto,
  AdminReportDto,
  AdminReportListResponse,
  ReportCountsDto,
  ReportedContentDto,
  ReviewReportRequest,
  ReportStatus,
  ReportReason,
  ReportTargetType,
  AdminUserDto,
  AdminUserListResponse,
  ReviewCredentialRequest,
  CredentialListResponse,
  CredentialCountsDto,
  AuthResponse,
  LoginRequest,
  MfaEnrollRequest,
  MfaEnrollResponse,
  MfaLoginRequest,
  RefreshTokenRequest,
  UserDto,
} from './api/generated/boticaAPI.schemas';

// Value re-export, not `export type`: VerificationStatus is a const object as
// well as a union, and consumers need the members by name now that the API
// serialises enums by name rather than by ordinal.
export { VerificationStatus } from './api/generated/boticaAPI.schemas';

import type {
  LoginRequest,
  MfaEnrollRequest,
  MfaLoginRequest,
  ReviewCredentialRequest,
  ReviewReportRequest,
  ReportStatus,
  VerificationStatus,
} from './api/generated/boticaAPI.schemas';

const {
  postApiAuthAdminLogin,
  postApiAuthAdminLoginMfa,
  postApiAuthAdminLoginMfaEnroll,
} = getAdminAuth();

const {
  getApiAdminCredentials,
  getApiAdminCredentialsId,
  postApiAdminCredentialsIdReview,
  getApiAdminUsers,
  getApiAdminUsersId,
  postApiAdminUsersIdPromote,
  postApiAdminUsersIdDemote,
  postApiAdminUsersIdSetFounder,
  postApiAdminUsersIdRemoveFounder,
  postApiAdminUsersIdSetVerifier,
  postApiAdminUsersIdRemoveVerifier,
} = getAdmin();

const { getApiCredentialsCredentialIdDocument } = getCredentials();

const {
  getApiAdminReports,
  getApiAdminReportsId,
  postApiAdminReportsIdReview,
} = getAdminReports();

export const authApi = {
  login: (body: LoginRequest) => postApiAuthAdminLogin(body),
  loginMfa: (body: MfaLoginRequest) => postApiAuthAdminLoginMfa(body),
  enrollMfa: (body: MfaEnrollRequest) => postApiAuthAdminLoginMfaEnroll(body),
};

export const adminApi = {
  getCredentials: (status?: VerificationStatus) =>
    getApiAdminCredentials(status !== undefined ? { status } : undefined),

  getCredentialById: (credentialId: string) =>
    getApiAdminCredentialsId(credentialId),

  reviewCredential: (credentialId: string, review: ReviewCredentialRequest) =>
    postApiAdminCredentialsIdReview(credentialId, review),

  // Returns a Blob of the credential image. Admin access is permitted by the
  // backend RBAC check; this call also fires the admin audit log server-side.
  getCredentialDocument: (credentialId: string) =>
    getApiCredentialsCredentialIdDocument(credentialId),

  getReports: (status?: ReportStatus) =>
    getApiAdminReports(status !== undefined ? { status } : undefined),

  // Detail carries the reported content. The server audit-logs this view with
  // the reviewer's IP before returning it — a reported message is two
  // pharmacists' private correspondence.
  getReportById: (reportId: string) => getApiAdminReportsId(reportId),

  reviewReport: (reportId: string, review: ReviewReportRequest) =>
    postApiAdminReportsIdReview(reportId, review),

  getUsers: (params?: { page?: number; pageSize?: number; search?: string; isAdmin?: boolean }) =>
    getApiAdminUsers(params),

  getUserById: (userId: string) => getApiAdminUsersId(userId),

  promoteToAdmin: (userId: string) => postApiAdminUsersIdPromote(userId),

  demoteFromAdmin: (userId: string) => postApiAdminUsersIdDemote(userId),

  setFounder: (userId: string) => postApiAdminUsersIdSetFounder(userId),

  removeFounder: (userId: string) => postApiAdminUsersIdRemoveFounder(userId),

  setVerifier: (userId: string) => postApiAdminUsersIdSetVerifier(userId),

  removeVerifier: (userId: string) => postApiAdminUsersIdRemoveVerifier(userId),
};
